import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isMobile } from 'react-device-detect';
import { useObjectsDispatch } from '../../context/objects';
import css from '../../styles/modules/objects/ObjectsCatalogSort.module.scss';

import Sort from '../../public/images/sort.svg';

const ObjectsCatalogSort = ({ content }) => {
  /** STATES */
  const setObjectsState = useObjectsDispatch();
  const [isVisible, setVisibility] = useState(false);
  const [selected, setSelected] = useState(content.filter((value) => value.selected)[0]);

  /** REFS */
  const parentEl = useRef(null);
  const selectEl = useRef(null);

  /** HANDLERS */
  const handleOutsideClick = (e) => {
    if (e.target.closest(`.${css.sort}`) !== parentEl.current)
      setVisibility(false);
  };

  /** HOOKS */
  useEffect(() => {
    document.body.addEventListener('click', handleOutsideClick);
    return () => document.body.removeEventListener('click', handleOutsideClick);
  }, []);

  /** TEMPLATES */
  if (isMobile)
    return (
      <button
        ref={parentEl}
        type="button"
        className={css.sort}
        onClick={() => selectEl.current.focus()}
      >
        <select
          ref={selectEl}
          onChange={(e) => {
            const selectedOption = [...e.target.options].filter((x) => x.selected)[0];
            const groupedSelected = {
              text: selectedOption.innerText,
              sort: selectedOption.value,
              direction: selectedOption.dataset.direction
            };

            setSelected(groupedSelected);
            setObjectsState({ type: 'SET_PAGINATION', payload: { page: 1 } });
            setObjectsState({
              type: 'SET_SORTING',
              payload: {
                sort: groupedSelected.sort,
                sortDirection: groupedSelected.direction
              }
            });
          }}
        >
          {content.map((value, index) => (
            <option
              key={index}
              data-direction={value.direction}
              value={value.sort}
              defaultValue={value.selected ? 'selected' : null}
            >
              {value.text}
            </option>
          ))}
        </select>
        <div className={css.sortShowcase}>
          <div className={css.sortText}>{selected.text}</div>
          <Sort />
        </div>
      </button>
    );

  return (
    <div ref={parentEl} className={css.sort}>
      <div
        className={css.sortShowcase}
        role="button"
        tabIndex={0}
        onClick={() => setVisibility(!isVisible)}
      >
        <div className={css.sortText}>{selected.text}</div>
        <Sort />
      </div>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
            className={css.sortPicker}
          >
            {content.map((value, index) => (
              <button
                type="button"
                className={[
                  css.sortElement,
                  value.sort === selected.sort && value.direction === selected.direction
                    ? css.selected
                    : null
                ].join(' ')}
                key={index}
                onClick={(e) => {
                  setVisibility(false);

                  if (!e.target.classList.contains(css.selected)) {
                    setSelected(value);
                    setObjectsState({ type: 'SET_PAGINATION', payload: { page: 1 } });
                    setObjectsState({
                      type: 'SET_SORTING',
                      payload: { sort: value.sort, sortDirection: value.direction }
                    });
                  }
                }}
              >
                {value.text}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ObjectsCatalogSort;
